import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@curves/ui-base';
import { useHistory } from 'react-router-dom';
import { useMainLayoutContext } from '@Local/Components/Layout/Context/MainLaydoutContext';
import { FOOTER_TYPE, HEADER_TYPE, THEME_TYPE } from '@Constants/dataEnums';
import {
    MEMBER_CYCLE_PURCHASE_PRODUCT_SELECT_ROUTER,
    MEMBER_CYCLE_PURCHASE_PENDING_CHANGE_ROUTER,
} from '@Router/routerPath';
import { CyclePurchaseOrderCard } from '@Local/CyclePurchase/Components';
import useMemberCyclePurchaseApi from '@Apis/useMemberCyclePurchaseApi';

/**
 * 循環訂單列表頁
 * 顯示會員所有有效循環訂單，提供「變更訂購商品」及「查看預約變更」入口。
 * API 回應前以 initialized 旗標隱藏內容，避免空畫面閃爍。
 */
const CyclePurchasePage = () => {
    const { setHeaderType, setFooterType, setThemeType } = useMainLayoutContext();
    const history = useHistory();
    const { getMemberCyclePurchaseListApi } = useMemberCyclePurchaseApi();

    // 功能：循環訂單清單
    const [orders, setOrders] = useState([]);
    // 功能：資料載入完成旗標（防止 API 回應前顯示空畫面）
    const [initialized, setInitialized] = useState(false);

    // 功能：設定版面（Header / Footer / 主題）
    useEffect(() => {
        setHeaderType(HEADER_TYPE.LOGO_WHITE | HEADER_TYPE.HIDE_BACKGROUND | HEADER_TYPE.UNFIXED);
        setFooterType(FOOTER_TYPE.HIDE);
        setThemeType(THEME_TYPE.DARK);
    }, [setHeaderType, setFooterType, setThemeType]);

    // 功能：載入循環訂單清單，完成後（成功或失敗）翻轉 initialized 以顯示內容
    useEffect(() => {
        let isCancelled = false;
        const fetchOrders = async () => {
            try {
                const data = await getMemberCyclePurchaseListApi();
                if (!isCancelled) {
                    if (data) setOrders(data);
                    setInitialized(true);
                }
            } catch (e) {
                if (isCancelled) return;
                setInitialized(true);
            }
        };
        fetchOrders();
        return () => {
            isCancelled = true;
        };
    }, [getMemberCyclePurchaseListApi]);

    // 功能：點擊「變更訂購商品」，將目前訂單商品轉為初始購物車，前往選品頁
    const handleEdit = (order) => {
        const sourceCart = Object.fromEntries(order.items.map((i) => [i.productID, i.quantity]));
        history.push(MEMBER_CYCLE_PURCHASE_PRODUCT_SELECT_ROUTER, { order, sourceCart });
    };

    // 功能：點擊「查看預約變更」，前往預約變更歷史頁
    const handleViewHistory = (order) => {
        history.push(MEMBER_CYCLE_PURCHASE_PENDING_CHANGE_ROUTER, { order });
    };

    return (
        <Box className="cycle-purchase-page">
            <Box className="container">
                <Typography className="cp-page-title">我的循環訂單</Typography>

                {initialized && (
                    <>
                        <Box className="cp-page-footnote">
                            <Typography component="span" className="cp-footnote-text">
                                目前僅提供變更蛋白口味
                                <ul>
                                    <li><strong>‧ 不可變更益生菌與鈣品項數量</strong></li>
                                    <li><strong>‧ 不可變動原蛋白購買數量</strong></li>
                                </ul>
                                如需其他訂單服務，請洽店舖教練協助。
                            </Typography>
                        </Box>
                        
                        {orders.length === 0 ? (
                            <Box className="cp-empty-state">
                                <Typography className="cp-empty-text">目前沒有有效的循環訂單</Typography>
                            </Box>
                        ) : (
                            <Box className="cp-order-list">
                                {orders.map((order) => (
                                    <CyclePurchaseOrderCard
                                        key={order.cycleOrderID}
                                        order={order}
                                        onEdit={handleEdit}
                                        onViewHistory={handleViewHistory}
                                    />
                                ))}
                            </Box>
                        )}

                    </>
                )}
            </Box>
        </Box>
    );
};

export default CyclePurchasePage;
